<template>
  <div class="mailbox-verification-instructions-google-business">
    <el-progress
      :percentage="stepPercentage"
      :show-text="false"
      class="progress-bar"
      color="#21b963"
    />
    <el-carousel
      v-if="normalAutoForwarding"
      ref="googleNormCarousel"
      :autoplay="false"
      :loop="false"
      direction="vertical"
      indicator-position="none"
      arrow="always"
      @change="(val) => updateStep(val)">
      <el-carousel-item>
        <h3>
          <img class="logo" src="../logos/gmail-logo.svg">
          <a href="https://accounts.google.com/Logout?hl=en&continue=https://mail.google.com/mail&service=mail" target="_blank" class="just-a-link">Click here to sign in to Gmail</a> as {{forwardingFromAddress}}
        </h3>
        <p>
          After you've signed in, <strong>come back to this tab</strong> and click the button below.
        </p>
        <p class="footnote">
          Don't have <strong>{{forwardingFromAddress}}</strong> set up as a Google Workspace user? 
          <a href="#" class="just-a-link" @click.stop.prevent="normalAutoForwarding=false">Click here</a>.
        </p>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've signed in to Gmail</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Copy this email address by clicking on it.</h3>
        <i class="ddi ddi-arrow-down bouncy" />
        <clipboard-text :text="incomingEmailAddress" class="incoming-email-address" @click="emailCopied=true" />
        <p><br></p>
        <p>
          This is the email address for this mailbox. In a moment, you'll paste it into Gmail so Google knows to 
          auto-forward future emails here.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div v-if="emailCopied" class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">Continue to the next step!</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Add the auto-forwarding address.</h3>
        <div class="step-wrapper">
          <div class="step">
            <span>1</span>
            <p>
              <strong><a href="https://mail.google.com/mail/u/0/#settings/fwdandpop" target="_blank" class="just-a-link">Click here</a></strong>
              to open a new tab to Gmail's auto-forwarding section.
            </p>
          </div>
          <div class="step">
            <span>2</span>
            <p>
              In <strong>Forwarding</strong>, click <strong>"Add a Forwarding Address"</strong> and paste the copied address.
            </p>
          </div>
          <div class="step">
            <span>3</span>
            <p>
              Click <strong>Next</strong>, <strong>Proceed</strong>, then <strong>OK</strong>.
              A confirmation link will be sent to this mailbox.
            </p>
          </div>
        </div>
        <img src="./img/mb-google-business-1.jpg">
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've done this. Find my confirmation link!</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <mailbox-verification-gmail-confirmation-link-finder
          v-if="isConfirmationEmailStep"
          @go-back-clicked="goToFirstItem"
          @confirmed="goToNextItem"
        />
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Complete the auto-forwarding setup.</h3>
        <div class="step-wrapper">
          <div class="step">
            <span>1</span>
            <p>
              <strong><a href="https://mail.google.com/mail/u/0/#settings/fwdandpop" target="_blank" class="just-a-link">Click here</a></strong>
              to go back to Gmail's auto-forwarding section.
            </p>
          </div>
          <div class="step">
            <span>2</span>
            <p>
              In the <strong>Forwarding</strong> section, check the <strong>"Forward a copy of incoming mail to"</strong> option.
            </p>
          </div>
          <div class="step">
            <span>3</span>
            <p>
              Select <strong>{{ incomingEmailAddress }}</strong> in the dropdown.
            </p>
          </div>
          <div class="step">
            <span>4</span>
            <p>
              Make sure you click <strong>"Save Changes"</strong> at the bottom of the page.
            </p>
          </div>
        </div>
        <img src="./img/mb-google-business-2.jpg">
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've completed the steps and clicked "Save Changes" at the bottom.</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>You're ready to test auto-forwarding!</h3>
        <p>
          Now that you've completed all the previous steps, we'll run a quick test to see that
          emails from <strong>{{forwardingFromAddress}}</strong> are forwarding to this mailbox.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button :color="ColorAssistant.MailboxColor" @click="$emit('completion-clicked')">Test that auto-forwarding is working!</dd-button>
        </div>
      </el-carousel-item>
    </el-carousel>
    <el-carousel
      v-else
      ref="googleRoutingCarousel"
      :autoplay="false"
      :loop="false"
      direction="vertical"
      indicator-position="none"
      arrow="always"
      @change="(val) => updateStep(val)">
      <el-carousel-item>
        <h3>
          <img class="logo" src="../logos/gmail-logo.svg">
          Forward emails with Default Routing in Gmail.
        </h3>
        <p>
          Use <strong>Default Routing</strong> to route all emails from <strong>{{forwardingFromAddress}}</strong>
          to this mailbox without creating a new user in your Google account.
        </p>
        <p class="footnote">
          Already have <strong>{{forwardingFromAddress}}</strong> set up as a Google Workspace user? 
          <a href="#" class="just-a-link" @click.stop.prevent="normalAutoForwarding=true">Click here</a>.
        </p>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">Set up Default Routing</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3><a href="https://accounts.google.com/Logout?hl=en&continue=https://admin.google.com" target="_blank" class="just-a-link">Click here to sign in</a> to Google Admin.</h3>
        <p>
          After you've signed in, <strong>come back to this tab</strong> and click the button below.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've signed in to Google Admin</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Next, <a href="https://admin.google.com/ac/apps/gmail/defaultrouting" target="_blank" class="just-a-link">click here to go to Default Routing</a>.</h3>
        <p>
          This is where we'll set up <strong>{{forwardingFromAddress}}</strong> to route emails to this mailbox.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I'm in Default Routing</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Copy this email address by clicking on it.</h3>
        <i class="ddi ddi-arrow-down bouncy" />
        <clipboard-text :text="incomingEmailAddress" class="incoming-email-address" @click="emailCopied=true" />
        <p><br></p>
        <p>
          This is the email address for this mailbox. In a moment, you'll paste it into Gmail so Google knows to 
          auto-forward future emails here.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div v-if="emailCopied" class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">Continue to the next step!</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Create a new routing rule.</h3>
        <p>
          Click the <strong>Add Rule / Add Another Rule</strong> button on the lower-right.<br>
          A pop-up form should appear.
        </p>
        <p><br></p>
        <img src="./img/mb-google-dr-1.jpg">
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I see the pop-up.</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Fill out the pop-up form.</h3>
        <div class="step-wrapper">
          <div class="step">
            <span>1</span>
            <p>
              Under "1. Specify envelope recipients to match", select <strong>Single Recipient</strong>.
              Then enter <strong>{{forwardingFromAddress}}</strong>.
            </p>
          </div>
          <div class="step">
            <span>2</span>
            <p>
              Under "2. If the envelope matches the above, do the following", select <strong>Modify Message</strong>, then
              scroll to <strong>Envelope recipient</strong>. Select <strong>Change envelope recipient</strong>. 
              Then, paste the address you just copied under the <strong>Replace recipient</strong> field.
            </p>
          </div>
          <div class="step">
            <span>3</span>
            <p>
              Under "3. Options", select <strong>Perform this action on non-recognized and recognized addresses</strong>.
            </p>
          </div>
          <div class="step">
            <span>4</span>
            <p> 
              Finally, click <strong>Save</strong> at the bottom of the form.
            </p>
          </div>
        </div>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've completed the form.</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>You're ready to test auto-forwarding!</h3>
        <p>
          Now that you've completed all the previous steps, we'll run a quick test to see that
          emails from <strong>{{forwardingFromAddress}}</strong> are forwarding to this mailbox.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button :color="ColorAssistant.MailboxColor" @click="$emit('completion-clicked')">Test that auto-forwarding is working!</dd-button>
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>


<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import MailboxVerificationGmailConfirmationLinkFinder from './mailbox-verification-gmail-confirmation-link-finder.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */

export default defineComponent({
  name: 'MailboxVerificationInstructionsGoogleBusiness',
  components: {
    MailboxVerificationGmailConfirmationLinkFinder,
  },
  props: {
    incomingEmailAddress: {
      type: String,
      required: true,
    },
    forwardingFromAddress: {
      type: String,
      required: true,
    },
  },
  emits: ['completion-clicked'],
  data() {
    return {
      ColorAssistant,
      curStep: 1,
      emailCopied: false,
      normalAutoForwarding: true,
    }
  },
  computed: {
    stepPercentage(): number {
      return Math.round(((this.curStep - 1) / (this.stepCount - 1)) * 100)
    },
    isConfirmationEmailStep(): boolean {
      return this.curStep === 4
    },
    currentRef(): any {
      return this.normalAutoForwarding
        ? this.$refs.googleNormCarousel
        : this.$refs.googleRoutingCarousel
    },
    stepCount(): number {
      return this.normalAutoForwarding ? 6 : 7
    },
  },
  methods: {
    updateStep(curItem: any) {
      this.curStep = curItem + 1
      // Reset copied email...
      this.emailCopied = false
    },
    goToNextItem() {
      // @ts-ignore
      this.currentRef.setActiveItem(this.curStep)
    },
    goToPrevItem() {
      // @ts-ignore
      this.currentRef.setActiveItem(this.curStep - 2)
    },
    goToFirstItem() {
      // @ts-ignore
      this.currentRef.setActiveItem(0)
    },
  },
})
</script>

<style lang="scss" >
.mailbox-verification-instructions-google-business {
  h3 {
    position: relative;

    > img.logo {
      position: absolute;
      top: -42px;
      border: none !important;
      height: 28px;
      margin: 0 auto;
      left: 0;
      right: 0;
    }
  }
  .footnote + .bottom-centered {
    position: absolute;
    bottom: 60px;
  }
  .footnote {
    position: absolute;
    bottom: 0;
    @include lettering--p-sm;
    color: var(--color-text-third);

    > a {
      color: inherit;
    }
  }
}
</style>
